import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { adminAuthHeader } from "@/hooks/useAdminAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * 4-state CRM toggle for the lead's preferred contact time-slot:
 *   "Mattina" → "Pome" → "Indifferente" → "?" → "Mattina" …
 *
 * "?" = not asked yet (default for legacy rows or new users). Every state
 * has a distinct colour so the admin can scan the column at a glance.
 */
const STATES = ["mattina", "pome", "indifferente", "?"];
const STYLES = {
  mattina:      { bg: "#fef9c3", border: "#fde68a", color: "#854d0e", label: "Mattina" },
  pome:         { bg: "#e0e7ff", border: "#a5b4fc", color: "#3730a3", label: "Pome" },
  indifferente: { bg: "#f0f9ff", border: "#7dd3fc", color: "#075985", label: "Indifferente" },
  "?":          { bg: "#f1f5f9", border: "#cbd5e1", color: "#475569", label: "?" },
};

// Legacy values from the previous "quando contattare" schema — we map them
// all to "?" since their meaning under the new schema is undefined.
const LEGACY_TO_UNKNOWN = new Set(["oggi", "domani", "dopodomani"]);

export default function ContactWhenToggle({ userId, value, onChange }) {
  const [saving, setSaving] = useState(false);
  const current = STATES.includes(value) ? value :
    (LEGACY_TO_UNKNOWN.has(value) ? "?" : "?");
  const next = STATES[(STATES.indexOf(current) + 1) % STATES.length];
  const s = STYLES[current];

  const flip = async (e) => {
    e?.stopPropagation();
    if (saving) return;
    setSaving(true);
    try {
      await axios.patch(
        `${API}/admin/users/${userId}/contact-when`,
        { contact_when: next },
        { headers: adminAuthHeader() },
      );
      onChange?.(userId, next);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Errore";
      toast.error(`Impossibile aggiornare: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <button
      type="button"
      onClick={flip}
      disabled={saving}
      data-testid={`contact-when-toggle-${userId}`}
      title={`Fascia preferita: ${s.label} — clicca per passare a "${STYLES[next].label}"`}
      className="text-[11px] font-semibold px-2.5 py-1 rounded-full border transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
      style={{ background: s.bg, borderColor: s.border, color: s.color }}
    >
      {s.label}
    </button>
  );
}
